import { useMutation, useReactiveVar } from '@apollo/client';
import { Typography } from '@mui/material';
import type React from 'react';
import allFieldMapsConfigQuery from 'src/api/admin/allFieldMapsConfigQuery';
import deleteFieldMapsConfigMutation from 'src/api/admin/deleteFieldMapsConfigMutation';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';

interface DeleteFieldMapsConfigMutationVariables {
  fieldMapConfigId: string;
}

interface DeleteFieldMapsConfigMutationResponse {
  deleteFieldMapsConfig: {
    success: boolean;
  };
}

const FieldMapsDeleteModal: React.FC = () => {
  const viewState = useReactiveVar(viewStateVar);
  const { toastr } = useCustomToast();

  const [deleteFieldMapsConfig] = useMutation<
    DeleteFieldMapsConfigMutationResponse,
    DeleteFieldMapsConfigMutationVariables
  >(deleteFieldMapsConfigMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [allFieldMapsConfigQuery],
  });

  const deleteFieldItemHandler = (): void => {
    deleteFieldMapsConfig({
      variables: {
        fieldMapConfigId: viewState.deleteFieldMapsConfigId,
      },
    });
    viewStateVar({
      ...viewStateVar(),
      fieldMapsDeleteDialog: null,
    });
    toastr.success(`Field '${viewState.deleteFieldMapsConfigName}' was deleted successfully`);
  };

  const cancelDeleteHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      fieldMapsDeleteDialog: null,
    });
  };

  return (
    <AkomiDialog
      title="Delete"
      maxWidth="xs"
      open={!!viewState.fieldMapsDeleteDialog}
      onClose={cancelDeleteHandler}
      actions={
        <AkomiDialogActions
          variant="delete-cancel"
          primaryLabel="Delete"
          onPrimary={deleteFieldItemHandler}
          onSecondary={cancelDeleteHandler}
        />
      }
    >
      <Typography>
        Are you sure you want to delete &ldquo;{viewState.deleteFieldMapsConfigName}
        &rdquo;?
      </Typography>
    </AkomiDialog>
  );
};

export default FieldMapsDeleteModal;
